// Single-accent → per-section variants (Task: Single Accent Color).
//
// The owner picks ONE accent color; the app derives a "light-section" variant
// (readable on the white middle band of the public website) and a
// "dark-section" variant (readable on the black hero/footer) and stores them
// in the existing accentColorLight / accentColorDark columns. Everything
// downstream (public Vault template, AccentColorSync, studio backdrops) keeps
// reading those two columns unchanged.
//
// Shared between client (live preview while typing) and server (tests/tools),
// so this module must stay dependency-free and side-effect-free.

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === "string" && HEX_RE.test(value);
}

// For hex TEXT inputs: users often paste "1F1F1F" without the "#". Prepend it
// as soon as the value is unambiguously hex digits (partial or full, up to 6)
// so both "#1F1F1F" and "1F1F1F" work. Everything else passes through
// untouched — validation stays with isHexColor at the consumer.
export function normalizeHexInput(raw: string): string {
  const v = raw.trim();
  if (/^[0-9a-fA-F]{1,6}$/.test(v)) return `#${v}`;
  return v;
}

function parseHex(hex: string): [number, number, number] | null {
  if (!isHexColor(hex)) return null;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(r: number, g: number, b: number): string {
  return (
    "#" + [r, g, b].map((v) => clampByte(v).toString(16).padStart(2, "0")).join("")
  );
}

// Blend an rgb triple toward a target channel value (0 = black, 255 = white).
function mixToward(
  [r, g, b]: [number, number, number],
  target: number,
  amount: number,
): [number, number, number] {
  return [
    r + (target - r) * amount,
    g + (target - g) * amount,
    b + (target - b) * amount,
  ];
}

// WCAG relative luminance (0 = black, 1 = white).
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const chan = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

// Luminance ceilings/floors chosen so the variants clear roughly a 3:1
// contrast ratio against their section background (white or black) while
// staying recognizably "the same color". Colors already inside the band pass
// through unchanged.
const LIGHT_SECTION_MAX_LUMINANCE = 0.3; // on white: must be dark enough
const DARK_SECTION_MIN_LUMINANCE = 0.15; // on black: must be bright enough
const STEP = 0.12;
const MAX_STEPS = 12;

export type AccentVariants = {
  /** Variant safe on the WHITE middle band (stored as accentColorLight). */
  light: string;
  /** Variant safe on the BLACK hero/footer (stored as accentColorDark). */
  dark: string;
};

/**
 * Derive the light-section and dark-section variants from one accent hex.
 * Returns null when the input isn't a full #rrggbb hex (e.g. mid-typing).
 */
export function deriveAccentVariants(accent: string): AccentVariants | null {
  const rgb = parseHex(accent);
  if (!rgb) return null;

  let light = rgb;
  for (
    let i = 0;
    i < MAX_STEPS && relativeLuminance(light) > LIGHT_SECTION_MAX_LUMINANCE;
    i++
  ) {
    light = mixToward(light, 0, STEP);
  }

  let dark = rgb;
  for (
    let i = 0;
    i < MAX_STEPS && relativeLuminance(dark) < DARK_SECTION_MIN_LUMINANCE;
    i++
  ) {
    dark = mixToward(dark, 255, STEP);
  }

  return { light: toHex(...light), dark: toHex(...dark) };
}

/**
 * The vault template's default DARK-section accent (config.theme.accentDark)
 * — the color dark-band accents (hero/footer bars, footer brand text) fall
 * back to when a profile has no valid accent at all. One shared constant so
 * the React footers, the server-rendered legal pages, and /api/sales-footer
 * can never drift apart on the fallback.
 */
export const DEFAULT_DARK_ACCENT = "#F4AB15";

/**
 * Resolve the single "base" accent to show in the one accent input from a
 * business-profile payload. Prefers the stored base `accentColor`, but the
 * legacy default "#000000" (pre-single-accent rows never touched it) falls
 * back to the light-section value so existing users see the color they
 * actually picked. A user who genuinely chooses black via the new UI also
 * derives accentColorLight = #000000, so the fallback shows black for them
 * too — no visible inconsistency.
 */
export function resolveBaseAccent(p: {
  accentColor?: string;
  accentColorLight?: string;
  accentColorDark?: string;
}): string {
  if (isHexColor(p.accentColor) && p.accentColor.toLowerCase() !== "#000000") {
    return p.accentColor;
  }
  if (isHexColor(p.accentColorLight)) return p.accentColorLight;
  if (isHexColor(p.accentColorDark)) return p.accentColorDark;
  if (isHexColor(p.accentColor)) return p.accentColor;
  return "#7A8B6F";
}
